/**
 * Конфигурация языков Seguro Tenerife.
 *
 * Источник правды для списка поддерживаемых языков, порядка в переключателе,
 * маппинга кодов браузера и ключа localStorage. Держим отдельно от init,
 * чтобы значения можно было импортировать в UI (переключатель языков) без
 * подтягивания всего инстанса i18next.
 *
 * Языки и поведение портированы из прототипа /Users/mike/Desktop/fun/index.html.
 */

/**
 * Код языка приложения.
 * ВНИМАНИЕ: украинский — это код `uk` (ISO 639-1), а НЕ `ua`. В UI он
 * подписывается как «UA», но внутренний код всегда `uk`.
 */
export type AppLocale = 'en' | 'es' | 'uk' | 'ru';

/**
 * Порядок языков в переключателе: EN, ES, UA, RU (требование продукта).
 * Используется для рендера кнопок переключателя в неизменном порядке.
 */
export const LOCALE_ORDER: readonly AppLocale[] = ['en', 'es', 'uk', 'ru'] as const;

/** Множество поддерживаемых кодов для быстрой проверки. */
export const SUPPORTED_LOCALES: readonly AppLocale[] = ['en', 'es', 'uk', 'ru'] as const;

/**
 * Базовый язык. По i18n-стандарту SOUL все ключи сначала заполняются на en,
 * и при отсутствии перевода показываем именно en (а не пустую строку).
 */
export const FALLBACK_LOCALE: AppLocale = 'en';

/**
 * Подписи языков для кнопок переключателя.
 * Украинский подписан «UA» по требованию продукта, хотя его код — `uk`.
 */
export const LOCALE_LABELS: Record<AppLocale, string> = {
  en: 'EN',
  es: 'ES',
  uk: 'UA',
  ru: 'RU',
};

/**
 * Маппинг двухбуквенного кода из браузера в код приложения.
 * Особый случай: белорусский (`be`) маппится на русский (`ru`) — так в прототипе.
 * Всё, чего нет в карте, обрабатывается фоллбэком на en (см. detectLocale).
 */
export const BROWSER_LANG_MAP: Record<string, AppLocale> = {
  be: 'ru',
  ru: 'ru',
  uk: 'uk',
  en: 'en',
  es: 'es',
};

/** Ключ в localStorage, под которым сохраняется выбор языка пользователя. */
export const LOCALE_STORAGE_KEY = 'seguro_lang';
